import { NetworkType, useIntl } from '@capsicum/common';
import { Alert, Button, ButtonSection, ModalService, Tooltip, useRouter } from '@capsicum/common-web';
import { AlertIcon } from '@capsicum/common-web/icons';
import {
	RECEIVE_NETWORK_LABELS,
	RECEIVE_NETWORK_SHORTCODES,
	type ReceiveNetworks,
	TabNames
} from '@capsicum/features/wallet/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { FTUpgradeDrawer, type NetworkCardItem, NetworkSelectionListCard } from '../../ui';
import { S } from '../styles';

const networks: Array<NetworkCardItem> = [
	{
		label: RECEIVE_NETWORK_LABELS[NetworkType.CC2 as ReceiveNetworks],
		name: NetworkType.CC2 as ReceiveNetworks
	},
	{
		label: RECEIVE_NETWORK_LABELS[NetworkType.CC1 as ReceiveNetworks],
		name: NetworkType.CC1 as ReceiveNetworks,
		shortCode: RECEIVE_NETWORK_SHORTCODES[NetworkType.CC1 as ReceiveNetworks]
	}
];

export const Fantokens = () => {
	const { push } = useRouter();
	const [selectedNetwork, setSelectedNetwork] = useState<string | undefined>(undefined);
	const [isButtonDisabled, setButtonDisabled] = useState<boolean>(true);

	const getNetworkStatus = useCallback(
		(networkId: string) => {
			if (selectedNetwork === networkId) {
				return 'selected';
			}

			return 'unselected';
		},
		[selectedNetwork]
	);

	useEffect(() => {
		if (!selectedNetwork) {
			return;
		}
		setButtonDisabled(false);
	}, [selectedNetwork, setSelectedNetwork]);

	const { $t } = useIntl();

	const title = $t('wallet.receive.selectNetwork.title');
	const subtitle = $t('wallet.receive.selectNetwork.subtitle');
	const warning = $t('wallet.receive.selectNetwork.warning');
	const button = $t('wallet.receive.selectNetwork.button');
	const tooltipTitle = $t('wallet.receive.selectNetwork.tooltipTitle');
	const tooltipText = $t('wallet.receive.selectNetwork.tooltipTextFantokens');

	const networkCards = useMemo(
		() =>
			networks?.map(network => {
				return (
					<NetworkSelectionListCard
						item={network}
						key={network.name}
						selectNetwork={setSelectedNetwork}
						status={getNetworkStatus(network.name)}
					/>
				);
			}),
		[getNetworkStatus]
	);

	const onContinue = () => {
		selectedNetwork === NetworkType.CC1
			? ModalService.addModal({
					detached: false,
					isClosable: false,
					renderComponent: ({ close }) => (
						<FTUpgradeDrawer
							testID="receive-ftUpgrade-drawer"
							onButtonPress={() => {
								push(`/wallet/receive/fantokens/address?network=${selectedNetwork}`);
								close();
							}}
							onSecondaryActionOnButtonPress={() => {
								push(`/wallet?tab=${TabNames.FAN_TOKENS}`);
								close();
							}}
						/>
					)
				})
			: push(`/wallet/receive/fantokens/address?network=${selectedNetwork}`);
	};

	return (
		<S.Container>
			<S.Title>{title}</S.Title>
			<S.Subtitle>
				{subtitle}
				<Tooltip contentPosition="bottom_right">
					<Tooltip.Trigger testID="receiveFT-selectNetwork-tooltip" />
					<Tooltip.Content>
						<Tooltip.Title>{tooltipTitle}</Tooltip.Title>
						<Tooltip.Description>{tooltipText}</Tooltip.Description>
					</Tooltip.Content>
				</Tooltip>
			</S.Subtitle>
			{networkCards}
			<Alert
				closable={false}
				icon={AlertIcon}
				subTitle={warning}
				testID="receiveFT-selectNetwork-warning"
				title=""
				type="important"
			/>
			<ButtonSection>
				<Button
					color="brand"
					disabled={isButtonDisabled}
					testID="receiveFT-selectNetwork-button"
					text={button}
					variant="default"
					onClick={onContinue}
				/>
			</ButtonSection>
		</S.Container>
	);
};
